import { useEffect, useState } from 'react'

interface CacheItem<T> {
  data: T
  timestamp: number
}

interface CacheConfig {
  maxAge: number
  maxSize: number
}

class Cache<T> {
  private cache: Map<string, CacheItem<T>>
  private config: CacheConfig

  constructor(config: CacheConfig) {
    this.cache = new Map()
    this.config = config
  }

  set(key: string, data: T): void {
    while (this.cache.size >= this.config.maxSize) {
      const oldestEntry = Array.from(this.cache.entries()).reduce(
        (oldest, current) => {
          return !oldest || current[1].timestamp < oldest[1].timestamp
            ? current
            : oldest
        }
      )

      if (oldestEntry) {
        this.cache.delete(oldestEntry[0])
      }
    }

    this.cache.set(key, {
      data,
      timestamp: Date.now(),
    })
  }

  get(key: string): T | undefined {
    const item = this.cache.get(key)
    if (!item) return undefined

    if (Date.now() - item.timestamp > this.config.maxAge) {
      this.cache.delete(key)
      return undefined
    }

    return item.data
  }

  clear(): void {
    this.cache.clear()
  }
}

// Create data cache that can store any type of data
export const dataCache = new Cache<unknown>({
  maxAge: 5 * 60 * 1000, // 5 minutes
  maxSize: 100,
})

// Type-safe wrapper for cache operations
export function createCachedQuery<T>(
  queryFn: () => Promise<T>,
  cacheKey: string,
  cache: Cache<T> = dataCache as Cache<T>
): () => Promise<T> {
  return async (): Promise<T> => {
    const cached = cache.get(cacheKey) as T | null
    if (cached !== null) return cached

    const data = await queryFn()
    cache.set(cacheKey, data)
    return data
  }
}

// Image cache implementation
export class ImageCache {
  private static CACHE_NAME = 'app-image-cache-v1'
  private static maxAge = 24 * 60 * 60 * 1000 // 24 hours

  static async get(url: string): Promise<string | null> {
    try {
      const cache = await caches.open(this.CACHE_NAME)
      const response = await cache.match(url)

      if (!response) return null

      const cachedAt = response.headers.get('cached-at')
      if (cachedAt && Date.now() - Number(cachedAt) > this.maxAge) {
        await cache.delete(url)
        return null
      }

      return URL.createObjectURL(await response.blob())
    } catch (error) {
      console.error('Error accessing image cache:', error)
      return null
    }
  }

  static async set(url: string, response: Response): Promise<void> {
    try {
      const cache = await caches.open(this.CACHE_NAME)
      const headers = new Headers(response.headers)
      headers.set('cached-at', Date.now().toString())

      const cachedResponse = new Response(await response.blob(), {
        headers,
        status: response.status,
        statusText: response.statusText,
      })

      await cache.put(url, cachedResponse)
    } catch (error) {
      console.error('Error caching image:', error)
    }
  }

  static async clearOld(): Promise<void> {
    try {
      const cache = await caches.open(this.CACHE_NAME)
      const keys = await cache.keys()

      for (const request of keys) {
        const response = await cache.match(request)
        if (!response) continue

        const cachedAt = response.headers.get('cached-at')
        if (cachedAt && Date.now() - Number(cachedAt) > this.maxAge) {
          await cache.delete(request)
        }
      }
    } catch (error) {
      console.error('Error clearing old cache:', error)
    }
  }
}

// React hook for cached images
export function useCachedImage(url: string | undefined): string | undefined {
  const [cachedUrl, setCachedUrl] = useState<string | undefined>(undefined)

  useEffect(() => {
    if (!url) {
      setCachedUrl(undefined)
      return
    }

    let isMounted = true

    const loadImage = async () => {
      const cached = await ImageCache.get(url)
      if (cached && isMounted) {
        setCachedUrl(cached)
        return
      }

      try {
        const response = await fetch(url)
        const clonedResponse = response.clone()
        await ImageCache.set(url, clonedResponse)

        if (isMounted) {
          setCachedUrl(URL.createObjectURL(await response.blob()))
        }
      } catch (error) {
        console.error('Error loading image:', error)
        if (isMounted) setCachedUrl(url)
      }
    }

    loadImage()

    return () => {
      isMounted = false
    }
  }, [url])

  return cachedUrl
}
